import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useLingui } from "@lingui/react/macro";
import { Loader2, RotateCcw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AppDialog } from "@/components/common/AppDialog";
import { useAuth } from "@/hooks/useAuth";
import {
  maintenanceApi,
  RESTORE_DEFAULT_ROLES_PHRASE,
  type SystemRoleRestoreEntry,
  type SystemRolesRestoreResponse,
} from "@/lib/api/services/maintenance.api";
import { getApiErrorMessage } from "@/lib/api/api";

/**
 * Restore the shipped default roles to their canonical permissions.
 *
 * Default roles are seed data, so a database that has been hand-edited — or one
 * seeded before the permission vocabulary was corrected — drifts away from what
 * the API enforces, and the permission matrix then shows grants that decide
 * nothing. This is `pnpm db:sync-roles` over HTTP, with a diff first.
 *
 * Same shape as the tenant purge, one tier down in blast radius: preview (no
 * writes) → a dialog restating what changes → the exact phrase retyped → apply.
 * The phrase is warranted because this rewrites authorization and can strip an
 * operator of permissions they were leaning on; it is recoverable by re-granting
 * by hand, which a purge is not.
 *
 * Two entry points render this — the Support → Cleanup tab and a button in the
 * Roles & Permissions header — so the mutation, the copy and the confirmation
 * all live here and are shared, rather than reimplemented per call site.
 */

/** Custom and org-scoped roles are never touched; only these are rewritten. */
export function useRestoreDefaultRoles() {
  const { t } = useLingui();
  const queryClient = useQueryClient();

  const [preview, setPreview] = useState<SystemRolesRestoreResponse | null>(null);
  const [result, setResult] = useState<SystemRolesRestoreResponse | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [phrase, setPhrase] = useState("");

  const previewMutation = useMutation({
    mutationFn: maintenanceApi.previewSystemRoles,
    onSuccess: (response) => setPreview(response),
    onError: (error) => toast.error(getApiErrorMessage(error, t`Failed to load preview`)),
  });

  const applyMutation = useMutation({
    mutationFn: () =>
      maintenanceApi.applySystemRoles({ confirm: true, confirmationPhrase: phrase }),
    onSuccess: (response) => {
      setResult(response);
      setPreview(null);
      setConfirmOpen(false);
      setPhrase("");
      // The matrix, the role list and every role dialog are now stale, and so is
      // the signed-in user's own permission set.
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      queryClient.invalidateQueries({ queryKey: ["auth", "user"] });
      toast.success(t`Default roles restored`);
    },
    onError: (error) =>
      toast.error(getApiErrorMessage(error, t`Restore failed — roles are unchanged`)),
  });

  const openConfirm = () => {
    setPhrase("");
    setConfirmOpen(true);
    // The dialog shows the diff; fetch it if the operator skipped Preview.
    if (!preview && !previewMutation.isPending) previewMutation.mutate();
  };

  return {
    preview,
    result,
    confirmOpen,
    setConfirmOpen,
    phrase,
    setPhrase,
    previewMutation,
    applyMutation,
    openConfirm,
    phraseMatches: phrase === RESTORE_DEFAULT_ROLES_PHRASE,
  };
}

type RestoreState = ReturnType<typeof useRestoreDefaultRoles>;

function RoleDiff({ entry }: { entry: SystemRoleRestoreEntry }) {
  const { t } = useLingui();

  return (
    <div className="space-y-1.5 border-b border-(--color-border) py-2.5 last:border-b-0">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">{entry.name}</span>
        <span className="font-mono text-[11px] text-(--color-text-secondary)">{entry.code}</span>
        {entry.status === "missing" && (
          <span className="rounded-full bg-(--color-warning-bg) px-2 py-0.5 text-[10px] font-medium text-(--color-warning-text)">
            {t`will be recreated`}
          </span>
        )}
        {entry.status === "in-sync" && (
          <span className="text-[11px] text-(--color-text-secondary)">{t`no change`}</span>
        )}
      </div>
      {(entry.added.length > 0 || entry.removed.length > 0) && (
        <div className="flex flex-wrap gap-1">
          {entry.added.map((permission) => (
            <span
              key={`add-${permission}`}
              className="rounded bg-(--color-success-bg) px-1.5 py-0.5 font-mono text-[11px] text-(--color-success-text)"
            >
              +{permission}
            </span>
          ))}
          {entry.removed.map((permission) => (
            <span
              key={`remove-${permission}`}
              className="rounded bg-(--color-danger-bg) px-1.5 py-0.5 font-mono text-[11px] text-(--color-danger-text)"
            >
              −{permission}
            </span>
          ))}
        </div>
      )}
      {entry.status === "drifted" && entry.added.length === 0 && entry.removed.length === 0 && (
        <p className="text-[11px] text-(--color-text-secondary)">
          {t`Permissions match; the role's name or active flag will be reset.`}
        </p>
      )}
    </div>
  );
}

function RestoreSummaryLine({ response }: { response: SystemRolesRestoreResponse }) {
  const { t } = useLingui();
  const { rolesDrifted, rolesMissing, rolesInSync, permissionsAdded, permissionsRemoved } =
    response.summary;

  if (rolesDrifted === 0 && rolesMissing === 0) {
    return (
      <p className="flex items-center gap-2 text-sm text-(--color-text-secondary)">
        <ShieldCheck className="size-4 text-(--color-success-text)" />
        {t`All ${rolesInSync} default roles already match the shipped definition.`}
      </p>
    );
  }

  return (
    <p className="text-sm text-(--color-text-secondary)">
      {t`${rolesDrifted} drifted, ${rolesMissing} missing, ${rolesInSync} already correct — ${permissionsAdded} permissions added, ${permissionsRemoved} removed.`}
    </p>
  );
}

/** The diff list, shared by the card body and the confirmation dialog. */
function RoleDiffList({ response }: { response: SystemRolesRestoreResponse }) {
  return (
    <div className="rounded-md border border-(--color-border) px-3">
      {response.details.roles.map((entry) => (
        <RoleDiff key={entry.code} entry={entry} />
      ))}
    </div>
  );
}

function ConfirmDialog({ state }: { state: RestoreState }) {
  const { t } = useLingui();
  const {
    preview,
    confirmOpen,
    setConfirmOpen,
    phrase,
    setPhrase,
    applyMutation,
    phraseMatches,
  } = state;

  return (
    <AppDialog
      open={confirmOpen}
      onOpenChange={(open) => {
        if (!applyMutation.isPending) setConfirmOpen(open);
      }}
      title={t`Restore default permissions?`}
      description={t`Every default role is rewritten to its shipped permissions and the access policies are rebuilt from it. Custom roles and organization-scoped roles are not touched. This can sign you out of permissions you granted by hand.`}
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => setConfirmOpen(false)}
            disabled={applyMutation.isPending}
          >
            {t`Cancel`}
          </Button>
          <Button
            disabled={!phraseMatches || applyMutation.isPending}
            onClick={() => applyMutation.mutate()}
            className="gap-2"
          >
            {applyMutation.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <RotateCcw className="size-4" />
            )}
            {t`Restore defaults`}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {preview ? (
          <div className="space-y-3">
            <RestoreSummaryLine response={preview} />
            <div className="max-h-72 overflow-y-auto">
              <RoleDiffList response={preview} />
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-sm text-(--color-text-secondary)">
            <Loader2 className="size-4 animate-spin" />
            {t`Working out what would change…`}
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="restore-roles-confirm-phrase">
            {t`Type ${RESTORE_DEFAULT_ROLES_PHRASE} to confirm`}
          </Label>
          <Input
            id="restore-roles-confirm-phrase"
            value={phrase}
            onChange={(e) => setPhrase(e.target.value)}
            placeholder={RESTORE_DEFAULT_ROLES_PHRASE}
            autoComplete="off"
            disabled={applyMutation.isPending}
          />
        </div>
      </div>
    </AppDialog>
  );
}

/**
 * The compact entry point, for the Roles & Permissions header. Same mutation
 * and same confirmation as the card — only the surrounding chrome differs.
 */
export function RestoreDefaultRolesButton() {
  const { t } = useLingui();
  const { hasRole } = useAuth();
  const state = useRestoreDefaultRoles();

  // Both endpoints are gated on the wildcard permission, which only a super
  // admin holds. Hide rather than explain: this header is not the place for it.
  if (!hasRole("SUPER_ADMIN")) return null;

  return (
    <>
      <Button variant="outline" onClick={state.openConfirm} className="gap-2">
        <RotateCcw className="size-4" />
        {t`Restore default permissions`}
      </Button>
      <ConfirmDialog state={state} />
    </>
  );
}

export function RestoreDefaultRolesCard() {
  const { t } = useLingui();
  const { hasRole } = useAuth();
  const state = useRestoreDefaultRoles();
  const { preview, result, previewMutation, openConfirm } = state;

  const canRestore = hasRole("SUPER_ADMIN");

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RotateCcw className="size-5" />
          {t`Restore default permissions`}
        </CardTitle>
        <CardDescription>
          {t`Resets all seven built-in roles to their canonical permissions, and recreates any that were deleted. Custom roles and organization-scoped roles are left alone. Use this when a role's permissions have been edited by hand, or when the permission matrix shows grants the API does not enforce.`}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!canRestore ? (
          <p className="text-sm text-(--color-text-secondary)">
            {t`Only an administrator with full access can restore the default roles.`}
          </p>
        ) : (
          <>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                disabled={previewMutation.isPending}
                onClick={() => previewMutation.mutate()}
                className="gap-2"
              >
                {previewMutation.isPending && <Loader2 className="size-4 animate-spin" />}
                {t`Preview changes`}
              </Button>
              <Button onClick={openConfirm} className="gap-2">
                <RotateCcw className="size-4" />
                {t`Restore defaults…`}
              </Button>
            </div>

            {preview && (
              <div className="space-y-3">
                <RestoreSummaryLine response={preview} />
                <RoleDiffList response={preview} />
              </div>
            )}

            {result && (
              <div className="space-y-3">
                <p className="text-sm font-medium">{t`Restore complete.`}</p>
                <RestoreSummaryLine response={result} />
                <RoleDiffList response={result} />
              </div>
            )}
          </>
        )}
      </CardContent>

      <ConfirmDialog state={state} />
    </Card>
  );
}
